import { Injectable } from '@angular/core';
import { groupBy } from 'lodash';
import {
    FilterInfo,
    FilterType,
    FilterIndexContext
} from '../core-data-model/FilterInfo.model';
import { SearchState, PageState } from '../core-data-model/SearchState.model';
import { SortInfo } from '../core-data-model/SortInfo.model';
import { InitDataService } from '../system-services/InitData/InitData.service';
import { UserProfileSettingsService } from '../system-services/UserProfileSettings/UserProfileSettings.service';

export class UnknownFilterTypeError extends Error {
    constructor() {
        super('Unknown Filter Type!');
    }
}

export type KeyValue = {
    key: string;
    value: any;
};

@Injectable({ providedIn: 'root' })
export class ODataQueryGeneratorService {
    constructor(
        public userProfile: UserProfileSettingsService,
        public initData: InitDataService
    ) {}

    createODataString(searchState: SearchState): string {
        const keyValuePairs = [];
        keyValuePairs.push(
            ...this.getPagingParameters(searchState.pageState),
            ...this.getSearchParameters(searchState.filterState),
            ...this.getFilterParameters(searchState.filterState),
            ...this.getOrderbyParameter(searchState.sortingState)
        );
        return this.combineParameters(keyValuePairs);
    }

    createSingleFieldSearch(value: string, fieldToSearch: string) {
        const keyValuePairs = [];
        keyValuePairs.push(
            ...this.getSingleFieldSearchParameters(value, fieldToSearch)
        );
        return this.combineParameters(keyValuePairs);
    }

    protected getPagingParameters(pagingData: PageState): KeyValue[] {
        if (!pagingData) {
            return [];
        }
        return [
            {
                key: '$skip',
                value:
                    (pagingData.currentPageNumber - 1) *
                    pagingData.resultsPerPage
            },
            {
                key: '$top',
                value: pagingData.resultsPerPage
            }
        ];
    }

    protected getSearchParameters(filterState: FilterInfo[]): KeyValue[] {
        if (!filterState) {
            return [];
        }
        const searchFilter = filterState.find(
            (filter: FilterInfo) => filter.type === FilterType.Search
        );
        if (searchFilter) {
            return [
                {
                    key: '$search',
                    value: encodeURIComponent(searchFilter.parameters.term)
                },
                {
                    key: 'searchfields',
                    value: this.getDefaultSearchFields().join(',')
                }
            ];
        }
        return [];
    }

    protected getDefaultSearchFields(): string[] {
        return [
            'peopleAndJobsSearchResult/sortName',
            'peopleAndJobsSearchResult/assessmentUser/email',
            'peopleAndJobsSearchResult/assessmentUser/lastName',
            'peopleAndJobsSearchResult/assessmentUser/firstName'
        ];
    }

    protected getSingleFieldSearchParameters(
        value: string,
        fieldToSearch: string
    ) {
        return [
            {
                key: '$search',
                value: value
            },
            {
                key: 'searchfields',
                value: `peopleAndJobsSearchResult/assessmentUser/${fieldToSearch}`
            }
        ];
    }

    protected getFilterParameters(filterState: FilterInfo[]): KeyValue[] {
        if (!filterState) {
            return [];
        }
        const filteredFilters = this.filterFilterList(filterState);
        const listOfListsOfFilters = Object.values(
            groupBy(filteredFilters, (filter) => filter.type.id)
        );
        return this.processFilterParameters(
            this.andListTogether(
                listOfListsOfFilters.map(
                    (filterListOfSingleType: FilterInfo[]) =>
                        this.orListTogether(
                            filterListOfSingleType.map((filterInfo) =>
                                filterInfo.getODataQuery(
                                    this.makeFilterContext()
                                )
                            )
                        )
                )
            )
        );
    }

    protected filterFilterList(filters: FilterInfo[]): FilterInfo[] {
        return filters.filter((filter) => filter.type !== FilterType.Search);
    }

    protected makeFilterContext() {
        return {
            indexContext: FilterIndexContext.PeopleAndJobs,
            showCognitive: this.userProfile.showCognitive,
            userGuid: this.initData.user.userGuid
        };
    }

    protected orListTogether(strings: string[]) {
        if (strings.length > 0) {
            return `(${strings.join(') or (')})`;
        }
        return '';
    }

    protected andListTogether(strings: string[]) {
        if (strings.length > 0) {
            return `(${strings.join(') and (')})`;
        }
        return '';
    }

    protected processFilterParameters(filterParam: string): KeyValue[] {
        if (filterParam) {
            return [
                {
                    key: '$filter',
                    value: filterParam
                }
            ];
        }
        return [];
    }

    // Implemented in specific files
    protected getOrderbyParameter(_sortingState: SortInfo[]): KeyValue[] {
        return [];
    }

    protected combineParameters(keyValuePairs: KeyValue[]): string {
        // TODO do some URL encoding?
        return keyValuePairs
            .map((pair) => `${pair.key}=${pair.value}`)
            .join('&');
    }
}
